import React, { useEffect, useState } from 'react'
import moment from 'moment'
import Alert from '@mui/material/Alert'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import MenuItem from '@mui/material/MenuItem'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import API from '../../../../services/API'
import { DOWNTIME_TYPES } from '../../vehicles/VehicleComponents/UnavailabilityDialog'

const toLocalInput = (value) =>
  value ? moment(value).format('YYYY-MM-DDTHH:mm') : ''

// Create or edit a planned downtime window for an asset — the asset-side
// mirror of the vehicle UnavailabilityDialog. The only conflicts are other
// windows on the same asset; the server returns them so the user can confirm
// anyway rather than getting an opaque rejection.
const AssetUnavailabilityDialog = ({ open, assetID, period, onClose, onSaved }) => {
  const [start, setStart] = useState('')
  const [end, setEnd] = useState('')
  const [reason, setReason] = useState('')
  const [type, setType] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [conflicts, setConflicts] = useState(null)

  useEffect(() => {
    if (!open) return
    setStart(toLocalInput(period?.start) || moment().format('YYYY-MM-DDTHH:mm'))
    setEnd(
      toLocalInput(period?.end) ||
        moment().add(1, 'day').format('YYYY-MM-DDTHH:mm'),
    )
    setReason(period?.reason || '')
    setType(period?.type || '')
    setError('')
    setConflicts(null)
  }, [open, period])

  const save = async (confirmed = false) => {
    if (!start || !end) {
      setError('Enter a start and end time.')
      return
    }
    if (!moment(end).isAfter(moment(start))) {
      setError('End time must be after the start time.')
      return
    }

    setSaving(true)
    setError('')
    try {
      const payload = {
        start: moment(start).toISOString(),
        end: moment(end).toISOString(),
        reason,
        type: type || undefined,
        confirmed,
      }
      const res = period?._id
        ? await API.put(`/fleet/asset-unavailability/${period._id}`, payload)
        : await API.post(`/fleet/assets/${assetID}/unavailability`, payload)

      if ([200, 201].includes(res.status)) {
        setConflicts(null)
        onSaved?.()
        onClose?.()
        return
      }

      if (res.status === 409 && res.data?.errorCode === 'hasConflicts') {
        setConflicts(res.data.conflicts || [])
        return
      }

      setError(res.data?.error || 'Unable to save this downtime window.')
    } catch (e) {
      if (e?.response?.status === 409 && e.response.data?.errorCode === 'hasConflicts') {
        setConflicts(e.response.data.conflicts || [])
      } else {
        setError(
          e?.response?.data?.error || 'Unable to save this downtime window.',
        )
      }
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onClose={saving ? null : onClose} fullWidth maxWidth="xs">
      <DialogTitle sx={{ fontWeight: 800 }}>
        {period ? 'Edit downtime' : 'Schedule downtime'}
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} pt={0.5}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField
            select
            label="Type"
            value={type}
            onChange={(e) => setType(e.target.value)}
            fullWidth
          >
            {DOWNTIME_TYPES.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
            <MenuItem value="">Unavailable (no planned work)</MenuItem>
          </TextField>
          <TextField
            label="Start"
            type="datetime-local"
            value={start}
            onChange={(e) => {
              setStart(e.target.value)
              setConflicts(null)
            }}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
          <TextField
            label="End"
            type="datetime-local"
            value={end}
            onChange={(e) => {
              setEnd(e.target.value)
              setConflicts(null)
            }}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
          <TextField
            label="Reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Booked in for annual calibration"
            multiline
            minRows={2}
            fullWidth
          />
          {conflicts && conflicts.length > 0 && (
            <Alert severity="warning">
              <Typography variant="body2" fontWeight={700} gutterBottom>
                This asset already has downtime during this time:
              </Typography>
              <Stack spacing={0.25}>
                {conflicts.map((conflict, index) => (
                  <Typography key={index} variant="caption" display="block">
                    • {conflict.label}
                  </Typography>
                ))}
              </Stack>
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit" disabled={saving}>
          Cancel
        </Button>
        {conflicts && conflicts.length > 0 ? (
          <Button
            variant="contained"
            color="warning"
            onClick={() => save(true)}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Continue Anyway'}
          </Button>
        ) : (
          <Button variant="contained" onClick={() => save(false)} disabled={saving}>
            {saving ? 'Saving...' : period ? 'Save Changes' : 'Schedule Downtime'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}

export default AssetUnavailabilityDialog
